import React from "react";
import { MdOutlineKeyboardDoubleArrowDown, MdDone } from "react-icons/md";
import { useTranslation } from "next-i18next";

const OurFeatures = () => {
  const { t } = useTranslation();
  const features = [
    {
      title: t("methodology-page.hero-slide-first-field-title"),
      color: "#FE3D67",
      items: [
        t("methodology-page.hero-slide-first-field-subtitle-1"),
        t("methodology-page.hero-slide-first-field-subtitle-2")
      ]
    },
    {
      title: t("methodology-page.hero-slide-second-field-title"),
      color: "#34C759",
      items: [
        t("methodology-page.hero-slide-second-field-subtitle-1"),
        t("methodology-page.hero-slide-second-field-subtitle-2")
      ]
    },
    {
      title: t("methodology-page.hero-slide-third-field-title"),
      color: "#007AFF",
      items: [
        t("methodology-page.hero-slide-third-field-subtitle")
      ]
    },
    {
      title: t("methodology-page.hero-slide-fourth-field-title"),
      color: "#BA3DE7",
      items: [
        t("methodology-page.hero-slide-fourth-field-subtitle-1"),
        t("methodology-page.hero-slide-fourth-field-subtitle-2"),
        t("methodology-page.hero-slide-fourth-field-subtitle-3"),
        t("methodology-page.hero-slide-fourth-field-subtitle-4"),
        t("methodology-page.hero-slide-fourth-field-subtitle-5"),
        t("methodology-page.hero-slide-fourth-field-subtitle-6")
      ]
    },
    {
      title: t("methodology-page.hero-slide-sixth-field-title-1"),
      color: "#D4B22B",
      items: [
        t("methodology-page.hero-slide-sixth-field-subtitle-1")
      ]
    }
  ];

  const FeatureCard = ({ title, items, color, borderColor }) => {
    return (
      <div
        className="relative rounded-[24px] overflow-hidden bg-[linear-gradient(80.42deg,rgba(255,255,255,0.08)_25.25%,rgba(83,84,108,0.1)_98.05%)] backdrop-blur-[16px]"
      >
        <div
          className="absolute inset-0 rounded-[24px] p-[1.2px] pointer-events-none"
          style={{
            background: borderColor
              ? "linear-gradient(160deg, rgba(29, 54, 118, 0.5) 44.76%, #3A9FDA 100%)"
              : "linear-gradient(185deg, rgba(29, 54, 118, 0.5) -1.41%, #3A9FDA 85.85%)",
            WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
            boxSizing: "border-box",
          }}
        ></div>
        <div className="px-[16px] py-[32px] md:p-[40px] flex flex-col relative">
          <div
            className="absolute opacity-60 w-[1.5px] left-[32px] md:left-[60px] top-[60px] md:top-[70px] bottom-[32px] md:bottom-8"
            style={{
              background: `linear-gradient(180deg, ${color} 0%, rgba(255, 255, 255, 0.01) 100%)`
            }}
          ></div>
          <div className="flex flex-row gap-[20px] relative z-10">
            <div
              className="md:min-w-[40px] md:h-[40px] min-w-[32px] h-[32px] rounded-full flex items-center justify-center mt-0 relative z-10 flex-shrink-0"
              style={{ backgroundColor: color }}
            >
              <MdOutlineKeyboardDoubleArrowDown className="text-white md:w-[30px] md:h-[30px] w-[24px] h-[24px]" />
            </div>
            <div className="flex flex-col gap-[24px] w-full">
              <h3 className="text-[16px] xs:text-[24px] md:text-[32px] font-[500] text-[#E5E5E5] leading-tight">
                {title}
              </h3>
              <div className="space-y-3 md:space-y-[24px]">
                {items.map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <span
                      className="min-w-[25px] min-h-[25px] rounded-full flex items-center justify-center relative"
                      style={{ '--bg-color': color }}
                    >
                      <span
                        className="absolute inset-0 rounded-full"
                        style={{ backgroundColor: 'var(--bg-color)', opacity: 0.4 }}
                      ></span>
                      <MdDone className="w-[18px] h-[18px] relative z-10" style={{ color: color }} />
                    </span>
                    <span className="text-[12px] xs:text-[14px] md:text-[16px] font-[400] text-[#E5E5E5] leading-relaxed">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section
      className="pt-[60px] pb-[60px] md:pt-[100px] md:pb-[150px] bg-gradient-to-b from-[#091F59] to-[#1A3884] md:rounded-[64px] rounded-[24px] mb-[100px] md:mb-[200px] mx-[8px]"
    >
      <div className="max-w-[90%] mx-auto">
        <div className="flex flex-col lg:justify-between items-center gap-6 lg:gap-12 mb-12">
          <div className="flex flex-col">
            <h2 className="text-[32px] md:text-[48px] lg:text-[64px] font-[500] text-white leading-tight text-center">
              {t("methodology-page.hero-slide-methodology-title-1")}
            </h2>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          <div className="flex flex-col gap-6 lg:gap-8 w-full lg:w-1/2 justify-between">
            {features.map((feature, index) => {
              if (index === 0 || index === 2 || index === 4) {
                return (
                  <FeatureCard
                    key={index}
                    title={feature.title}
                    items={feature.items}
                    color={feature.color}
                    borderColor={index === 4}
                  />
                );
              }
              return null;
            })}
          </div>
          <div className="flex flex-col gap-6 lg:gap-8 w-full lg:w-1/2 justify-between">
            {features.map((feature, index) => {
              if (index === 1 || index === 3) {
                return (
                  <FeatureCard
                    key={index}
                    title={feature.title}
                    items={feature.items}
                    color={feature.color}
                    borderColor={true}
                  />
                );
              }
              return null;
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurFeatures;
